import React from 'react';
import {
  Paper,
  Text,
  Group,
  Badge,
  Progress,
  Stack,
  Grid,
  ThemeIcon,
  SimpleGrid,
  ActionIcon,
  Tooltip
} from '@mantine/core';
import {
  IconTrendingUp,
  IconTrendingDown,
  IconMinus,
  IconEye,
  IconArrowRight
} from '@tabler/icons-react';
import { ComparisonAnalysis } from 'services/lia/overviewService';

/* eslint-disable */
interface ComparisonAnalysisCardProps {
  comparison: ComparisonAnalysis;
  onViewDetails?: (beforeId: string, afterId: string) => void;
}
/* eslint-enable */

const ComparisonAnalysisCard: React.FC<ComparisonAnalysisCardProps> = ({
  comparison,
  onViewDetails
}) => {
  const getImprovementColor = (improvement: number) => {
    if (improvement > 10) return 'green';
    if (improvement > 0) return 'blue';
    if (improvement < -10) return 'red';
    return 'yellow';
  };

  const getImprovementIcon = (improvement: number) => {
    if (improvement > 0) return <IconTrendingUp size={16} />;
    if (improvement < 0) return <IconTrendingDown size={16} />;
    return <IconMinus size={16} />;
  };

  const topImprovedParticipants = comparison.participantComparison
    .sort((a, b) => b.improvement - a.improvement)
    .slice(0, 3);

  const topImprovedModules = comparison.moduleComparison
    .sort((a, b) => b.improvement - a.improvement)
    .slice(0, 3);

  return (
    <Paper withBorder p='md' radius='md'>
      <Group justify='space-between' mb='md'>
        <div>
          <Text fw={600} size='lg'>
            Assessment Comparison
          </Text>
          <Group gap='xs' mt={4}>
            <Text size='sm' c='dimmed'>
              {comparison.beforeAssessmentName}
            </Text>
            <IconArrowRight size={14} />
            <Text size='sm' c='dimmed'>
              {comparison.afterAssessmentName}
            </Text>
          </Group>
        </div>
        <Group>
          <Badge
            color={getImprovementColor(comparison.improvementRate)}
            variant='filled'
            leftSection={getImprovementIcon(comparison.improvementRate)}
          >
            {comparison.improvementRate > 0 ? '+' : ''}
            {comparison.improvementRate.toFixed(1)}%
          </Badge>
          {onViewDetails && (
            <Tooltip label='View detailed comparison'>
              <ActionIcon
                variant='light'
                color='blue'
                onClick={() =>
                  onViewDetails(comparison.beforeAssessmentID, comparison.afterAssessmentID)
                }
              >
                <IconEye size={16} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Group>

      <Grid>
        <Grid.Col span={6}>
          <Paper withBorder p='sm' radius='sm'>
            <Text fw={500} size='sm' mb='xs'>
              Top Improved Participants
            </Text>
            <Stack gap='xs'>
              {topImprovedParticipants.map((participant) => (
                <Group key={participant.participantID} justify='space-between'>
                  <Text size='xs'>{participant.participantID}</Text>
                  <Group gap={4}>
                    <Text size='xs' c='dimmed'>
                      {participant.beforeScore} → {participant.afterScore}
                    </Text>
                    <ThemeIcon
                      size='xs'
                      color={getImprovementColor(participant.improvement)}
                      variant='light'
                    >
                      {getImprovementIcon(participant.improvement)}
                    </ThemeIcon>
                  </Group>
                </Group>
              ))}
            </Stack>
          </Paper>
        </Grid.Col>

        <Grid.Col span={6}>
          <Paper withBorder p='sm' radius='sm'>
            <Text fw={500} size='sm' mb='xs'>
              Module Improvements
            </Text>
            <Stack gap='xs'>
              {topImprovedModules.map((module) => (
                <div key={module.moduleID}>
                  <Group justify='space-between' mb={2}>
                    <Text size='xs' truncate>
                      {module.moduleName}
                    </Text>
                    <Text size='xs' fw={500}>
                      +{module.improvement.toFixed(1)}
                    </Text>
                  </Group>
                  <Progress
                    value={Math.min(Math.abs(module.improvement) * 5, 100)}
                    size='xs'
                    color={getImprovementColor(module.improvement)}
                  />
                </div>
              ))}
            </Stack>
          </Paper>
        </Grid.Col>
      </Grid>

      <SimpleGrid cols={3} mt='md' spacing='xs'>
        <Paper withBorder p='xs' radius='sm'>
          <Text size='xs' c='dimmed' ta='center'>
            Participants
          </Text>
          <Text fw={600} ta='center'>
            {comparison.participantComparison.length}
          </Text>
        </Paper>
        <Paper withBorder p='xs' radius='sm'>
          <Text size='xs' c='dimmed' ta='center'>
            Modules
          </Text>
          <Text fw={600} ta='center'>
            {comparison.moduleComparison.length}
          </Text>
        </Paper>
        <Paper withBorder p='xs' radius='sm'>
          <Text size='xs' c='dimmed' ta='center'>
            Avg. Improvement
          </Text>
          <Text fw={600} ta='center' c={getImprovementColor(comparison.improvementRate)}>
            {comparison.improvementRate > 0 ? '+' : ''}
            {comparison.improvementRate.toFixed(1)}%
          </Text>
        </Paper>
      </SimpleGrid>
    </Paper>
  );
};

/* eslint-disable */
interface ComparisonAnalysisListProps {
  comparisons: ComparisonAnalysis[];
  onViewDetails?: (beforeId: string, afterId: string) => void;
}
/* eslint-enable */

export const ComparisonAnalysisList: React.FC<ComparisonAnalysisListProps> = ({
  comparisons,
  onViewDetails
}) => {
  if (comparisons.length === 0) {
    return (
      <Paper withBorder p='xl' radius='md'>
        <Stack align='center' gap='md'>
          <ThemeIcon size={60} color='gray' variant='light'>
            <IconTrendingUp size={30} />
          </ThemeIcon>
          <div style={{ textAlign: 'center' }}>
            <Text fw={500} size='md' mb={4}>
              No Comparison Analysis Available
            </Text>
            <Text c='dimmed' size='sm'>
              Create follow-up assessments to track improvements over time.
            </Text>
          </div>
        </Stack>
      </Paper>
    );
  }

  return (
    <Stack gap='md'>
      {comparisons.map((comparison) => (
        <ComparisonAnalysisCard
          key={`${comparison.beforeAssessmentID}-${comparison.afterAssessmentID}`}
          comparison={comparison}
          onViewDetails={onViewDetails}
        />
      ))}

      {comparisons.length > 3 && (
        <Paper withBorder p='md' radius='md'>
          <Group justify='center'>
            <Text c='dimmed' size='sm'>
              Showing {Math.min(3, comparisons.length)} of {comparisons.length} comparisons
            </Text>
            {comparisons.length > 3 && (
              <Badge variant='light' color='blue'>
                +{comparisons.length - 3} more
              </Badge>
            )}
          </Group>
        </Paper>
      )}
    </Stack>
  );
};

export default ComparisonAnalysisCard;
